/* eslint-disable no-unused-vars */
// ControlRenderer.js
import React from "react";
import PropTypes from "prop-types";
import {
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import YesNoToggle from "../YesNoToggle/YesNoToggle";

const useStyles = makeStyles((theme) => ({
  dateField: {
    "& input": {
      cursor: "pointer",
    },
  },
  maskedTextField: {
    "& input": {
      fontFamily: "monospace",
      letterSpacing: "0.1em",
    },
  },
  disabledTextField: {
    "& .MuiInputBase-root.Mui-disabled": {
      color: "black", // Change this to your desired color
    },
  },
}));

const ControlRenderer = ({
  question,
  onChange,
  value,
  disabled,
  isMasked,
  isInvalid,
}) => {
  const classes = useStyles();

  // Check if this field should be masked (only if it's PII data and masking is enabled)
  const shouldMask = isMasked && question.is_pii_data === true;

  // Function to mask text (show only last 4 characters)
  const maskText = (text) => {
    if (!text) return text;
    const str = String(text);
    if (str.length <= 4) {
      // If 4 characters or less, mask all but keep format indication
      return "*".repeat(str.length);
    }
    // Show last 4 characters, mask the rest
    const maskedPart = "*".repeat(str.length - 4);
    const visiblePart = str.slice(-4);
    return maskedPart + visiblePart;
  };

  // Get display value based on mask state
  const getDisplayValue = (originalValue) => {
    if (shouldMask && originalValue) {
      return maskText(originalValue);
    }
    return originalValue || "";
  };

  const regexPattern = question.validation_regex
    ? question.validation_regex.replace(/\\\\/g, "\\")
    : null;
  const regex = regexPattern ? new RegExp(regexPattern) : null;
  const validationMessage = question.validation_message || "Invalid format";

  // USE FULL -->
  const handleTextChange = (e) => {
    let newValue = e.target.value;

    // Replace multiple spaces with single space
    newValue = newValue.replace(/\s{2,}/g, " ").trimStart();

    // If regex is provided by backend → use it strictly
    if (regex) {
      if (newValue === "") {
        onChange(question.id, "");
        return;
      }

      onChange(question.id, newValue);
      return;
    }

    const allowedPattern = /^[A-Za-z0-9, ]*$/;

    if (allowedPattern.test(newValue)) {
      onChange(question.id, newValue);
    }
  };

  const isRegexValid = regex ? regex.test(value) : true;
  const showError = isInvalid || (value && !isRegexValid);

  let helperText = "";
  if (isInvalid) {
    helperText = "This field is required";
  } else if (value && !isRegexValid) {
    helperText = validationMessage;
  }

  const inputType =
    question.validation_regex ||
    shouldMask ||
    question.control_type !== "number"
      ? "text"
      : "number";

  switch (question.control_type) {
    case "radio":
      return (
        <YesNoToggle
          checked={value || false}
          onChange={(e) => onChange(question.id, e.target.checked)}
          name={`question-${question.id}`}
          inputProps={{ "aria-label": "Yes No Toggle" }}
          disabled={disabled}
        />
      );

    case "text":
    case "number":
      return (
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          type={inputType}
          id={`question-${question.id}`}
          value={getDisplayValue(value)}
          onChange={handleTextChange}
          disabled={disabled || shouldMask}
          error={showError}
          helperText={helperText}
          className={`${shouldMask ? classes.maskedTextField : ""} ${
            classes.disabledTextField
          }`}
          autoComplete="off"
          placeholder={
            shouldMask ? "PII content masked (showing last 4 chars)" : ""
          }
        />
      );

    case "date": {
      const getInputDateValue = (val) => {
        if (!val) return "";

        // If in DD/MM/YYYY or DD-MM-YYYY format → convert to YYYY-MM-DD
        if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(val)) {
          const [day, month, year] = val.split(/[/-]/);
          return `${year}-${month}-${day}`;
        }

        // If already in YYYY-MM-DD → use as-is
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
          return val;
        }

        return "";
      };

      const handleDateChange = (e) => {
        const rawValue = e.target.value;
        if (!rawValue) {
          onChange(question.id, "");
          return;
        }

        const [year, month, day] = rawValue.split("-");
        const formatted = `${day}/${month}/${year}`;
        onChange(question.id, formatted);
      };

      return (
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          type="date"
          id={`question-${question.id}`}
          value={getInputDateValue(value)}
          onChange={handleDateChange}
          className={`${classes.dateField} ${classes.disabledTextField}`}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: new Date().toISOString().split("T")[0],
          }}
          autoComplete="off"
          disabled={disabled}
        />
      );
    }

    // case "checkbox":
    //   return (
    //     <div>
    //       <Typography variant="subtitle1">{question.label}</Typography>
    //       {question.details.map((detail) => (
    //         <FormControlLabel
    //           key={detail.id}
    //           control={
    //             <Checkbox
    //               checked={value && value.includes(detail.id)}
    //               onChange={(e) => onChange(question.id, detail.id, e.target.checked)}
    //               name={`question-${question.id}-${detail.id}`}
    //               disabled={disabled}
    //             />
    //           }
    //           label={detail.values}
    //         />
    //       ))}
    //     </div>
    //   );

    default:
      return (
        <Typography color="error">
          Unsupported control type: {question.control_type}
        </Typography>
      );
  }
};

ControlRenderer.propTypes = {
  question: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.any,
  disabled: PropTypes.bool,
  isMasked: PropTypes.bool,
  isInvalid: PropTypes.bool,
};

ControlRenderer.defaultProps = {
  disabled: false,
  isMasked: false,
};

export default ControlRenderer;
